import { useState, useMemo } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import styles from './ArticlesGrid.module.scss';
import { isBlogPost } from '../../../constants/categories';
import { getSiteUi } from '../../../lib/site-ui';

export default function ArticlesGrid({ categories = [], posts = [], languageCode = 'no' }) {
  const ui = getSiteUi(languageCode);
  const isEn = languageCode === 'en';
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [visibleCount, setVisibleCount] = useState(9);

  const ITEMS_PER_PAGE = 9;

  // Calculate category counts based on the actual filtered posts, not WordPress raw counts
  const categoriesWithCounts = useMemo(() => {
    const countMap = {};
    
    // Count posts per category based on the filtered article posts
    posts.forEach(post => {
      post.categories?.nodes?.forEach(cat => {
        countMap[cat.slug] = (countMap[cat.slug] || 0) + 1;
      });
    });
    
    // Map categories with recalculated counts, filtering out categories with 0 articles
    return categories
      .map(category => ({
        ...category,
        count: countMap[category.slug] || 0
      }))
      .filter(category => category.count > 0);
  }, [categories, posts]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setVisibleCount(ITEMS_PER_PAGE); // Reset visible count when changing category
  };

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + ITEMS_PER_PAGE);
  };

  const filteredPosts = selectedCategory === 'all' 
    ? posts 
    : posts.filter(post => post.categories?.nodes?.some(cat => cat.slug === selectedCategory));

  const visiblePosts = filteredPosts.slice(0, visibleCount);
  const hasMorePosts = filteredPosts.length > visibleCount;

  // Helper to get optimized image source
  const getOptimizedImageSrc = (featuredImage) => {
    if (!featuredImage?.node) return null;
    
    const sizes = featuredImage.node.mediaDetails?.sizes || [];
    
    // Look for medium_large (768px) or large (1024px) size for better quality thumbnails
    const mediumLarge = sizes.find(size => size.name === 'medium_large');
    const large = sizes.find(size => size.name === 'large');
    const medium = sizes.find(size => size.name === 'medium');
    
    // Return the best available size, fallback to mediaItemUrl
    return mediumLarge?.sourceUrl || large?.sourceUrl || medium?.sourceUrl || 
           featuredImage.node.mediaItemUrl || featuredImage.node.sourceUrl;
  };

  if (!posts?.length) return null;

  return (
    <section className={styles.articlesSection}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.categoriesWrapper}>
            {/* Desktop Categories */}
            <div className={styles.categories}>
              <button
                className={`${styles.categoryButton} ${selectedCategory === 'all' ? styles.active : ''}`}
                onClick={() => handleCategoryChange('all')}
              >
                {ui.allArticles} ({posts.length})
              </button>
              {categoriesWithCounts.map(category => (
                <button
                  key={category.id}
                  className={`${styles.categoryButton} ${selectedCategory === category.slug ? styles.active : ''}`}
                  onClick={() => handleCategoryChange(category.slug)}
                >
                  {category.name} ({category.count})
                </button>
              ))}
            </div>
            
            {/* Mobile Dropdown */}
            <select
              className={styles.mobileSelect}
              value={selectedCategory}
              onChange={(e) => handleCategoryChange(e.target.value)}
            >
              <option value="all">{ui.allArticles} ({posts.length})</option>
              {categoriesWithCounts.map(category => (
                <option key={category.id} value={category.slug}>
                  {category.name} ({category.count})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.articles}>
          {visiblePosts.map((post, index) => {
            const correctBasePath = isBlogPost(post)
              ? (isEn ? '/en/blog' : '/blogg')
              : (isEn ? '/en/articles' : '/artikler');
            const href = `${correctBasePath}/${post.slug}`;
            // Keep only first row as high-priority candidates
            const isPriority = index < 3;
            
            const imageSrc = getOptimizedImageSrc(post.featuredImage);
            
            return (
              <Link 
                key={post.id} 
                href={href}
                className={styles.articleCard}
              >
                <div className={styles.imageWrapper}>
                  {imageSrc && (
                    <Image
                      src={imageSrc}
                      alt={post.featuredImage?.node?.altText || post.title}
                      className={styles.image}
                      fill
                      sizes="(max-width: 480px) 95vw, (max-width: 768px) 48vw, (max-width: 1024px) 32vw, 350px"
                      priority={isPriority}
                      quality={75}
                      placeholder="blur"
                      blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzUwIiBoZWlnaHQ9IjI1MCIgdmVyc2lvbj0iMS4xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIzNTAiIGhlaWdodD0iMjUwIiBmaWxsPSIjZWVlIi8+PC9zdmc+"
                    />
                  )}
                </div>
                <div className={styles.content}>
                  <h3>{post.title}</h3>
                </div>
              </Link>
            );
          })}
        </div>

        {hasMorePosts && (
          <div className={styles.loadMoreWrapper}>
            <button 
              className={styles.loadMoreButton}
              onClick={handleLoadMore}
            >
              {ui.showMore}
            </button>
          </div>
        )}
      </div>
    </section>
  );
} 